/**
 * SMI / SAMI (Synchronized Accessible Media Interchange).
 *
 * Microsoft's 1998 format, and the only one in the launch set that is genuinely MULTI-TRACK:
 * `<P Class=ENUSCC>` and `<P Class=KRCC>` interleave two language streams in ONE file. A model
 * without a track axis reads a bilingual SAMI as one interleaved cue list and destroys one
 * language silently — which is why `TrackId` is in the contract from day one and why this
 * parser interns a track per class.
 *
 * THE STRUCTURAL FACT: `<SYNC Start=…>` is START-ONLY. There is no end time in the format at
 * all. An end is derived, and there are exactly two sources:
 *
 *   1. **The next `<SYNC>` carrying a paragraph of the SAME class.** Per-class, not global:
 *      in a bilingual file the English and Korean streams sync at different instants, and
 *      ending an English cue at the next Korean sync truncates it.
 *   2. **The `&nbsp;` terminator paragraph** — `<SYNC Start=5000><P Class=ENUSCC>&nbsp;` — the
 *      format's idiom for "this caption ends here and nothing replaces it". It ends the
 *      previous cue and produces NO cue of its own. Emitting it as a cue containing a
 *      non-breaking space is the classic SAMI bug: every player shows a blank subtitle box.
 *
 * A trailing cue with neither has nothing to derive from. It gets `LAST_CUE_TICKS` and a
 * diagnostic, because guessing silently is the failure mode this product exists to avoid and
 * dropping the last caption is worse.
 *
 * STYLING lives in the `<STYLE type="text/css">` block: `P { … }` for the base and `.ENUSCC {
 * Name: English; lang: en-US; SAMIType: CC; }` for each class. `Name` and `lang` become the
 * Track's name and language; every other property is preserved on `Style.rawColumns`, keyed by
 * the CSS property name, so an unmodelled property is reported rather than destroyed.
 */

import type {
  Color,
  Cue,
  ParseOptions,
  ParseResult,
  ParserDefinition,
  RunContext,
  Style,
  TextLine,
  TrackId,
} from '../../../contracts/cue-model.ts';
import { MS_GRID, makeCue, originFor, SYNTHETIC_FONT_SIZE } from '../../core/model/build.ts';
import { T } from '../../core/time/ticks.ts';
import { parseCssColor } from '../common/color.ts';
import { Draft, safeSpan } from '../common/draft.ts';
import { decodeEntities, decodeRuns, parseMarkup, SMI_MARKUP } from '../common/markup.ts';

/** How long a final cue with no following sync and no terminator lasts. Announced, never silent. */
const LAST_CUE_TICKS = 3 * 120_000;

const SYNC_RE = /<sync\b([^>]*)>/gi;
const P_RE = /<p\b([^>]*)>/gi;
const ATTR_RE = /([a-zA-Z-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/g;
const STYLE_BLOCK_RE = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/i;
const TITLE_RE = /<title\b[^>]*>([\s\S]*?)<\/title\s*>/i;
const CSS_RULE_RE = /([^{}]+)\{([^{}]*)\}/g;

interface Paragraph {
  readonly className: string;
  readonly body: string;
  readonly offset: number;
}

interface Sync {
  readonly start: number;
  readonly paragraphs: readonly Paragraph[];
  readonly offset: number;
}

export function parseSmi(text: string, opts: ParseOptions, ctx: RunContext): ParseResult {
  if (!/<sami\b/i.test(text) && !/<sync\b/i.test(text)) {
    return {
      ok: false,
      reason: 'not-recognized',
      detail: 'line 1: a SAMI file contains <SAMI> and <SYNC Start=…> elements; neither was found',
    };
  }

  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: 'smi', grid: MS_GRID });
  const lineOf = lineIndexer(text);

  const title = TITLE_RE.exec(text)?.[1]?.trim();
  if (title !== undefined && title !== '') draft.setTitle(decodeEntities(title));

  /*── <STYLE> — classes become tracks, properties become styles ────────────────*/

  const classInfo = new Map<
    string,
    { name: string; language: string | null; props: Record<string, string> }
  >();
  const styleBlock = STYLE_BLOCK_RE.exec(text)?.[1] ?? '';
  if (styleBlock.trim() !== '') {
    draft.addExtra('STYLE', styleBlock.replace(/<!--|-->/g, '').trim());
    CSS_RULE_RE.lastIndex = 0;
    let rule: RegExpExecArray | null = CSS_RULE_RE.exec(styleBlock);
    while (rule !== null) {
      const selector = (rule[1] ?? '').replace(/<!--|-->/g, '').trim();
      const body = rule[2] ?? '';
      const props: Record<string, string> = {};
      for (const decl of body.split(';')) {
        const colon = decl.indexOf(':');
        if (colon <= 0) continue;
        props[decl.slice(0, colon).trim().toLowerCase()] = decl.slice(colon + 1).trim();
      }
      if (selector.startsWith('.')) {
        const className = selector.slice(1).trim().toUpperCase();
        classInfo.set(className, {
          name: props['name'] ?? className,
          language: props['lang'] ?? null,
          props,
        });
      }
      rule = CSS_RULE_RE.exec(styleBlock);
    }
  }

  /*── <SYNC> scan ──────────────────────────────────────────────────────────────*/

  const syncs: Sync[] = [];
  SYNC_RE.lastIndex = 0;
  const marks: { start: number; attrs: string; from: number }[] = [];
  let m: RegExpExecArray | null = SYNC_RE.exec(text);
  while (m !== null) {
    marks.push({ start: m.index, attrs: m[1] ?? '', from: m.index + m[0].length });
    m = SYNC_RE.exec(text);
  }

  for (let i = 0; i < marks.length; i++) {
    const mark = marks[i] as { start: number; attrs: string; from: number };
    const to = i + 1 < marks.length ? (marks[i + 1] as { start: number }).start : text.length;
    const attrs = attributesOf(mark.attrs);
    const startRaw = attrs['start'];
    const start = startRaw === undefined ? Number.NaN : Number.parseInt(startRaw, 10);
    if (!Number.isFinite(start)) {
      draft.diag(
        'malformed-timestamp',
        `<SYNC> has no readable Start attribute (found ${JSON.stringify(mark.attrs.trim().slice(0, 40))})`,
        { sourceLine: lineOf(mark.start) },
      );
      continue;
    }
    const chunk = text.slice(mark.from, to);
    syncs.push({
      start: start * 120,
      paragraphs: paragraphsOf(chunk, mark.from),
      offset: mark.start,
    });
  }

  if (syncs.length === 0) {
    return draft.notRecognized('no readable <SYNC Start=…> element was found in this file');
  }

  syncs.sort((a, b) => a.start - b.start || a.offset - b.offset);

  /*── open-cue bookkeeping, PER CLASS ──────────────────────────────────────────*/

  interface Open {
    readonly start: number;
    readonly body: string;
    readonly offset: number;
    readonly trackId: TrackId;
    readonly styleId: Style['id'];
  }
  const open = new Map<string, Open>();
  const styleByClass = new Map<string, Style>();
  let ordinal = 0;

  const styleFor = (className: string): Style => {
    const hit = styleByClass.get(className);
    if (hit !== undefined) return hit;
    const info = classInfo.get(className);
    const props = info?.props ?? {};
    const style = draft.newStyle(info?.name ?? className, {
      fontName: props['font-family'] ?? 'Arial',
      // The synthetic default, not a literal of its own. Left at 48 after the synthetic style
      // moved to 16, a projected SMI→SMI write wrapped every cue in `<FONT size="48">`,
      // because project() inlines whatever a class changes against the synthetic style.
      fontSize: pxOr(props['font-size'], SYNTHETIC_FONT_SIZE),
      primaryColor: parseCssColor(props['color'] ?? '') ?? WHITE,
      bold: (props['font-weight'] ?? '').toLowerCase() === 'bold',
      italic: (props['font-style'] ?? '').toLowerCase() === 'italic',
      // Every CSS property we do not model, keyed by its own name. Without this an unmodelled
      // SAMI class property is destroyed at parse with nothing able to even report it.
      rawColumns: Object.fromEntries(Object.entries(props).filter(([k]) => !MODELLED_CSS.has(k))),
    });
    styleByClass.set(className, style);
    return style;
  };

  const emit = (className: string, endTicks: number): void => {
    const entry = open.get(className);
    if (entry === undefined) return;
    open.delete(className);
    const body = entry.body.trim();
    if (body === '') return;

    const id = ctx.alloc.cue();
    const time = safeSpan(entry.start, endTicks, draft, {
      cueId: id,
      sourceLine: lineOf(entry.offset),
    });
    const markup = parseMarkup({
      text: body,
      alloc: ctx.alloc,
      caps: SMI_MARKUP,
      cueStart: time.start,
      cueEnd: time.end,
    });
    const lines = decodeRuns(markup.lines) as TextLine[];
    const base = makeCue({
      id,
      time,
      lines,
      styleId: entry.styleId,
      trackId: entry.trackId,
      origin: { ...originFor(draft.inputId, 'parse', ordinal), sourceLine: lineOf(entry.offset) },
      granularity: 'coalesced',
    });
    draft.addCue(base as Cue);
    ordinal++;
  };

  for (const sync of syncs) {
    for (const paragraph of sync.paragraphs) {
      const className = paragraph.className === '' ? 'DEFAULT' : paragraph.className;
      // Any paragraph of a class ENDS that class's open cue, whatever it contains.
      emit(className, sync.start);

      const decoded = decodeEntities(paragraph.body).trim();
      // `&nbsp;` (U+00A0) alone is the terminator idiom, not a caption. So is an empty body.
      if (decoded === '' || /^[ \s]*$/u.test(decoded)) continue;

      const info = classInfo.get(className);
      const style = styleFor(className);
      const trackId = draft.trackFor(info?.name ?? className, info?.language ?? null, 'caption');
      open.set(className, {
        start: sync.start,
        body: paragraph.body,
        offset: paragraph.offset,
        trackId,
        styleId: style.id,
      });
    }
  }

  for (const className of [...open.keys()]) {
    const entry = open.get(className) as Open;
    draft.diag(
      'truncated-file',
      `the last <SYNC> for class ${className} has no following sync and no &nbsp; terminator; ` +
        `SAMI carries no end time, so ${LAST_CUE_TICKS / 120_000}s was used and is an assumption, not the file's content`,
      { sourceLine: lineOf(entry.offset) },
    );
    emit(className, entry.start + LAST_CUE_TICKS);
  }

  if (draft.cues.length === 0) {
    return draft.notRecognized('every <SYNC> in this file was empty or a &nbsp; terminator');
  }
  if (draft.tracks.length > 1) draft.setDuration(null);
  return draft.ok();
}

const WHITE: Color = { r: 255, g: 255, b: 255, a: 1 };

const MODELLED_CSS = new Set([
  'name',
  'lang',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
]);

function paragraphsOf(chunk: string, base: number): readonly Paragraph[] {
  const out: Paragraph[] = [];
  P_RE.lastIndex = 0;
  const found: { className: string; from: number; at: number }[] = [];
  let m: RegExpExecArray | null = P_RE.exec(chunk);
  while (m !== null) {
    const attrs = attributesOf(m[1] ?? '');
    found.push({
      className: (attrs['class'] ?? '').trim().toUpperCase(),
      from: m.index + m[0].length,
      at: m.index,
    });
    m = P_RE.exec(chunk);
  }
  if (found.length === 0) {
    // A `<SYNC>` with no `<P>` is legal in the wild; its content is the default class.
    const body = strip(chunk);
    if (body.trim() !== '') out.push({ className: '', body, offset: base });
    return out;
  }
  for (let i = 0; i < found.length; i++) {
    const p = found[i] as { className: string; from: number; at: number };
    const to = i + 1 < found.length ? (found[i + 1] as { at: number }).at : chunk.length;
    out.push({
      className: p.className,
      body: strip(chunk.slice(p.from, to)),
      offset: base + p.at,
    });
  }
  return out;
}

/** Removes the container tags SAMI wraps around content, leaving inline markup for the
 *  shared reader. `<br>` is turned into a newline HERE so the markup reader sees a break
 *  even in files that write `<BR>` with an explicit closing tag. */
function strip(chunk: string): string {
  return chunk
    .replace(/<\/?(?:body|sami|sync|p)\b[^>]*>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/^\s*\n/, '');
}

function attributesOf(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null = ATTR_RE.exec(raw);
  while (m !== null) {
    out[(m[1] as string).toLowerCase()] = m[3] ?? m[4] ?? m[5] ?? '';
    m = ATTR_RE.exec(raw);
  }
  return out;
}

function pxOr(value: string | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Character offset → 1-based line number. Built once per file; a linear scan per diagnostic
 *  would be O(n·d) on a file with many findings. */
function lineIndexer(text: string): (offset: number) => number {
  const starts: number[] = [0];
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) starts.push(i + 1);
  return (offset: number): number => {
    let lo = 0;
    let hi = starts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if ((starts[mid] as number) <= offset) lo = mid;
      else hi = mid - 1;
    }
    return lo + 1;
  };
}

export function sniffSmi(text: string, filename: string | null): number {
  const head = text.slice(0, 4096);
  const hasSami = /<sami\b/i.test(head);
  const hasSync = /<sync\b[^>]*\bstart\s*=/i.test(text);
  const ext = /\.sami?$/i.test(filename ?? '');
  if (hasSami && hasSync) return ext ? 1 : 0.95;
  if (hasSync) return ext ? 0.9 : 0.75;
  if (hasSami) return 0.5;
  return 0;
}

export const smiParser: ParserDefinition = {
  format: 'smi',
  sniff: sniffSmi,
  requiresFramerate: false,
  parse: parseSmi,
};

export { T };
